/**
 * RunStepList — the canonical per-step summary for a workflow run.
 *
 * Extracted from RunDetailsPanel so the flow-designer run preview and the
 * full-page run detail render an identical step summary (status, duration,
 * cache/thinking/variable/download badges, executed playbook, assertions,
 * downloads, data-script results, api_sequence call breakdowns, and a
 * what-the-agent-was-told hover on each row) and can't drift apart.
 *
 * The helpers (`formatStepStatus`, `isWarnAssertionStep`,
 * `nodeLabelWithStepsFallback`) are exported because RunDetailsPanel also uses
 * them outside the list (failed-step derivation, screenshots, wait state).
 *
 * @module ui/pages/FlowDesigner/runs/RunStepList
 */

import React from 'react';
import { Text, Tooltip, TooltipTrigger } from '@chatsdet/ui';
import { GearSix } from '@phosphor-icons/react';
import type { WorkflowAssertionResult, VideoTimeline } from '@chatsdet/types';
import type { WorkflowRunWithStepsDto, WorkflowRunStepDto } from '../hooks/useWorkflowRun';
import { matchStepTimelineAnchors } from '../../../components/workflowStepTimeline';
import type { WorkflowNode } from '../../../../types/workflow';
import { formatDuration } from './run-formatters';
import { StepPlaybook } from './StepPlaybook';
import {
  buildNodeIntrospectionView,
  getNodeIntrospection,
  parseNodeIntrospection,
} from './nodeIntrospection';
import { NodeIntrospectionTooltip } from './NodeIntrospectionTooltip';
import { StepAssertions, type AssertionIssueLinkContext } from './StepAssertions';
import { StepApiCalls } from './StepApiCalls';
import { StepCallFlow } from './StepCallFlow';
import { StepVariablesBadge } from './RunVariablesPanel';
import { getStepCacheBadge, getStepExecutionPhases } from './stepCacheBadge';
import { StepDownloads, StepDownloadsBadge, type StepDownloadArtifact } from './StepDownloads';
import { StepDataScripts, type DataScriptResult } from './StepDataScripts';
import { StepMcpTranscript, parseMcpTranscript } from './StepMcpTranscript';
import type { SerializedTrackedVariableDto } from '../hooks/useWorkflowRun';
import { nodeLabel } from '../utils/nodeLabel';

/** Resolve a human label for a step, falling back to persisted step metadata. */
export const nodeLabelWithStepsFallback = (
  nodes: WorkflowNode[],
  nodeId: string,
  steps?: WorkflowRunStepDto[]
) => {
  // For inlined setup snapshot nodes, fall back to persisted step metadata
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) {
    const step = steps?.find((s) => s.nodeId === nodeId);
    const meta = step?.metadataJson;
    if (meta && typeof meta.nodeName === 'string') return meta.nodeName;
    return `Step ${nodeId.slice(0, 6)}`;
  }
  return nodeLabel(nodes, nodeId);
};

export const formatStepStatus = (
  status: WorkflowRunWithStepsDto['steps'][number]['status'] | 'warning'
) => {
  switch (status) {
    case 'pending':
      return { label: 'Pending', tone: 'text-muted-foreground', dot: 'bg-muted' };
    case 'running':
      return { label: 'Running', tone: 'text-warning', dot: 'bg-warning' };
    case 'waiting':
      return { label: 'Waiting', tone: 'text-warning', dot: 'bg-warning' };
    case 'success':
      return { label: 'Success', tone: 'text-success', dot: 'bg-success' };
    case 'failed':
      return { label: 'Failed', tone: 'text-destructive', dot: 'bg-destructive' };
    case 'inconclusive':
      return { label: 'Platform error', tone: 'text-muted-foreground', dot: 'bg-muted-foreground' };
    case 'warning':
      return { label: 'Warning', tone: 'text-amber-600 dark:text-amber-400', dot: 'bg-amber-500' };
    case 'skipped':
      return { label: 'Skipped', tone: 'text-muted-foreground', dot: 'bg-muted' };
    default:
      return { label: status, tone: 'text-muted-foreground', dot: 'bg-muted' };
  }
};

/** Check if a step is a warn-severity assertion failure (non-blocking). */
export const isWarnAssertionStep = (step: WorkflowRunWithStepsDto['steps'][number]) =>
  step.status === 'failed' &&
  step.metadataJson?.nonBlocking === true &&
  step.metadataJson?.assertionSeverity === 'warn';

/**
 * Check if a `call_flow` step's child flow actually failed but `onFailure:
 * 'warn'` kept the step (and the parent run) green. Without this, the step
 * renders identically to a real success.
 */
export const isWarnedCallFlowStep = (step: WorkflowRunWithStepsDto['steps'][number]) =>
  step.status === 'success' && step.metadataJson?.warnedFailure === true;

/** Identify legacy runs whose automatic recovery erased their persisted step history. */
export const hasUnavailableRecoveredStepHistory = (
  steps: WorkflowRunStepDto[],
  metadataJson: Record<string, unknown> | null
): boolean => {
  if (steps.length > 0) return false;

  const recovery = metadataJson?.infrastructureRecovery;
  return (
    recovery !== null &&
    typeof recovery === 'object' &&
    !Array.isArray(recovery) &&
    (recovery as Record<string, unknown>).fullRunRetryAttempted === true
  );
};

export interface RunStepListProps {
  steps: WorkflowRunStepDto[];
  nodes: WorkflowNode[];
  workflowId: string | null;
  runId: string;
  /**
   * True when `nodes` come from the run-pinned workflow snapshot (see
   * `useRunSnapshotNodes`) — only then does the structured per-step popover
   * trust node config over the persisted introspection string.
   */
  nodesFromSnapshot?: boolean;
  /**
   * When provided together with `timeline`, each step row becomes a button that
   * seeks the video to that step's timeline anchor ("jump to step"). Omit both
   * for a static list (the flow-designer preview).
   */
  onSeekToStep?: (timestampMs: number) => void;
  timeline?: VideoTimeline | null;
  /** True for legacy recovered runs whose persisted step history was erased. */
  stepHistoryUnavailable?: boolean;
  /** Enables the "Link issue" action on failing assertion rows (see `StepAssertions`). */
  issueLinkContext?: AssertionIssueLinkContext;
}

/** The divided list of step rows. Caller supplies any surrounding heading. */
export const RunStepList: React.FC<RunStepListProps> = ({
  steps,
  nodes,
  workflowId,
  runId,
  nodesFromSnapshot = false,
  onSeekToStep,
  timeline,
  stepHistoryUnavailable = false,
  issueLinkContext,
}) => {
  const stepAnchors = React.useMemo(
    () => (onSeekToStep ? matchStepTimelineAnchors(steps, timeline ?? null) : null),
    [onSeekToStep, steps, timeline]
  );

  return (
    <div className="divide-y divide-border/50">
      {steps.length === 0 && stepHistoryUnavailable ? (
        <div className="rounded-md border border-border bg-muted/30 px-3 py-3">
          <Text size="xs" className="text-muted-foreground">
            Step details weren’t retained after this run was automatically recovered from a platform
            interruption.
          </Text>
        </div>
      ) : null}
      {steps.map((step, stepIdx) => {
        const displayStatus =
          isWarnAssertionStep(step) || isWarnedCallFlowStep(step)
            ? ('warning' as const)
            : step.status;
        const status = formatStepStatus(displayStatus);
        const replayDuration =
          typeof step.metadataJson?.replayDurationMs === 'number'
            ? step.metadataJson.replayDurationMs
            : null;
        const stepDurationMs =
          replayDuration ??
          (step.startedAt && step.finishedAt
            ? new Date(step.finishedAt).getTime() - new Date(step.startedAt).getTime()
            : null);

        const isSetupStep = step.metadataJson?.isSetupStep === true;
        const prevStep = stepIdx > 0 ? steps[stepIdx - 1] : null;
        const prevIsSetupStep = prevStep?.metadataJson?.isSetupStep === true;
        const setupFlowName =
          typeof step.metadataJson?.sourceSetupFlowName === 'string'
            ? step.metadataJson.sourceSetupFlowName
            : null;

        // Show group header when this is the first setup step in a consecutive group
        const showSetupGroupHeader = isSetupStep && !prevIsSetupStep;

        // Seek to the step's START marker so the video lands at the beginning of
        // the step (falls back to the primary anchor for steps that only have a
        // completed marker). `primary` still drives active-step detection.
        const seekTs =
          stepAnchors?.[stepIdx]?.started?.timestampMs ??
          stepAnchors?.[stepIdx]?.primary?.timestampMs ??
          null;
        const canSeek = Boolean(onSeekToStep) && seekTs !== null;

        const node = nodes.find((n) => n.id === step.nodeId);
        // Structured popover from node config — only when the node comes from
        // the run-pinned snapshot (execution-time truth). Otherwise fall back
        // to the persisted introspection string, then to the live definition
        // for pre-snapshot runs.
        const introspectionView = nodesFromSnapshot ? buildNodeIntrospectionView(node) : null;
        const introspectionText = introspectionView
          ? null
          : (step.metadataJson?.nodeIntrospection ?? getNodeIntrospection(node));
        const introspectionFallback = introspectionText
          ? parseNodeIntrospection(introspectionText, node?.type)
          : null;

        // The whole header line (label + status + duration) is the seek target,
        // so the obvious "row" click jumps the video to this step's start marker.
        const headerRow = (
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`h-2 w-2 shrink-0 rounded-full ${status.dot}`} aria-hidden="true" />
              <Text className="text-sm truncate">
                {nodeLabelWithStepsFallback(nodes, step.nodeId, steps)}
              </Text>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Text size="xs" className={status.tone}>
                {status.label}
              </Text>
              <Text size="xs" className="text-muted-foreground w-12 text-right">
                {formatDuration(stepDurationMs)}
              </Text>
            </div>
          </div>
        );

        const headerElement = canSeek ? (
          <button
            type="button"
            onClick={() => onSeekToStep?.(seekTs as number)}
            className="flex w-full rounded text-left hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {headerRow}
          </button>
        ) : (
          headerRow
        );

        return (
          <React.Fragment key={step.id}>
            {showSetupGroupHeader && (
              <div className="pt-2 pb-1 flex items-center gap-1.5">
                <GearSix size={12} className="text-muted-foreground shrink-0" />
                <Text size="xs" weight="medium" className="text-muted-foreground">
                  {setupFlowName ? `Setup: ${setupFlowName}` : 'Setup Flow'}
                </Text>
              </div>
            )}
            <div className={`py-1.5 ${isSetupStep ? 'pl-4' : ''}`}>
              {introspectionView || introspectionFallback ? (
                <TooltipTrigger delay={300}>
                  {headerElement}
                  <NodeIntrospectionTooltip
                    view={introspectionView}
                    fallback={introspectionFallback}
                  />
                </TooltipTrigger>
              ) : (
                headerElement
              )}
              {(() => {
                const assumptions = (step.metadataJson as Record<string, unknown> | null)
                  ?.assumptions;
                const hasAssumptions = Array.isArray(assumptions) && assumptions.length > 0;
                // Navigate steps are deterministic (no LLM turn), so the
                // complexity-derived thinking level is never meaningful here.
                const thinkingLevel =
                  node?.type === 'navigate' ? undefined : step.metadataJson?.thinkingLevel;
                const cacheOutcome = step.metadataJson?.cacheOutcome as string | undefined;
                const executionPhases = getStepExecutionPhases(step.metadataJson);
                // The phase chips subsume the bare "Agent" badge on cascaded
                // steps — without them the card silently merges the playbook's
                // rows with the agent's turn/assumption badges.
                const cacheBadge = executionPhases ? null : getStepCacheBadge(step.metadataJson);
                const stepVarsSet = (step.metadataJson?.variablesSet ??
                  []) as SerializedTrackedVariableDto[];
                const cacheProcessing = step.metadataJson?.cacheProcessingStatus as
                  | string
                  | undefined;
                const hasCacheProcessing = !!cacheProcessing && cacheProcessing !== 'completed';
                const downloadArtifacts = (step.metadataJson?.downloadArtifacts ??
                  []) as StepDownloadArtifact[];
                if (
                  !hasAssumptions &&
                  !thinkingLevel &&
                  !cacheOutcome &&
                  stepVarsSet.length === 0 &&
                  !hasCacheProcessing &&
                  downloadArtifacts.length === 0
                )
                  return null;
                return (
                  <div className="flex items-center gap-2 ml-4 mt-1 flex-wrap">
                    {executionPhases && (
                      <TooltipTrigger delay={0}>
                        <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-medium cursor-default">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                            {executionPhases.l2Label}
                          </span>
                          <span className="text-muted-foreground">→</span>
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                            {executionPhases.l3Label}
                          </span>
                        </span>
                        <Tooltip>
                          {executionPhases.ejectReason
                            ? `The playbook ran first, then handed off to a fresh agent: ${executionPhases.ejectReason}.`
                            : 'The playbook ran first, then handed off to a fresh agent.'}
                        </Tooltip>
                      </TooltipTrigger>
                    )}
                    {cacheBadge &&
                      (() => {
                        const badgeClassName =
                          cacheBadge.tone === 'recovery' || cacheBadge.tone === 'playbook'
                            ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                            : cacheBadge.tone === 'cached'
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                              : cacheBadge.tone === 'agent' &&
                                  step.metadataJson?.cacheOutcomeReason === 'replay_failed'
                                ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
                        return (
                          <TooltipTrigger delay={0}>
                            <span
                              className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium cursor-default ${badgeClassName}`}
                            >
                              {cacheBadge.label}
                            </span>
                            <Tooltip>{cacheBadge.tooltip}</Tooltip>
                          </TooltipTrigger>
                        );
                      })()}
                    {(() => {
                      const cp = step.metadataJson?.cacheProcessingStatus as string | undefined;
                      if (!cp || cp === 'completed') return null;
                      if (cp === 'failed') {
                        return (
                          <TooltipTrigger delay={0}>
                            <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 cursor-default">
                              Cache compile failed
                            </span>
                            <Tooltip>
                              Cache compilation failed during the run (heuristic selectors used).
                              Distinct from the async cache optimizer.
                            </Tooltip>
                          </TooltipTrigger>
                        );
                      }
                      return (
                        <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                          Compiling cache...
                        </span>
                      );
                    })()}
                    {stepVarsSet.length > 0 && <StepVariablesBadge variablesSet={stepVarsSet} />}
                    {downloadArtifacts.length > 0 && (
                      <StepDownloadsBadge count={downloadArtifacts.length} />
                    )}
                    {hasAssumptions && (
                      <TooltipTrigger delay={0}>
                        <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 cursor-default">
                          {(assumptions as unknown[]).length} assumption
                          {(assumptions as unknown[]).length !== 1 ? 's' : ''}
                        </span>
                        <Tooltip>
                          <div className="space-y-1 max-w-xs">
                            {(assumptions as string[]).map((a: string, i: number) => (
                              <div key={i} className="text-xs">
                                &bull; {a}
                              </div>
                            ))}
                          </div>
                        </Tooltip>
                      </TooltipTrigger>
                    )}
                    {thinkingLevel && (
                      <TooltipTrigger delay={0}>
                        <span
                          className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium cursor-default ${
                            thinkingLevel === 'high'
                              ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                              : thinkingLevel === 'medium'
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {thinkingLevel === 'high'
                            ? 'High'
                            : thinkingLevel === 'medium'
                              ? 'Med'
                              : 'Low'}
                        </span>
                        <Tooltip>{`Thinking: ${thinkingLevel}`}</Tooltip>
                      </TooltipTrigger>
                    )}
                  </div>
                );
              })()}
              {step.errorMessage && (
                <Text size="xs" className="text-destructive ml-4 mt-0.5">
                  {step.errorMessage}
                </Text>
              )}
              <StepPlaybook
                playbook={step.metadataJson?.executedPlaybook ?? []}
                outcomes={step.metadataJson?.stepOutcomes}
              />
              <StepAssertions
                results={(step.metadataJson?.assertionResults ?? []) as WorkflowAssertionResult[]}
                issueLinkContext={issueLinkContext}
              />
              <StepCallFlow metadataJson={step.metadataJson} />
              <StepDownloads
                artifacts={(step.metadataJson?.downloadArtifacts ?? []) as StepDownloadArtifact[]}
              />
              <StepDataScripts
                results={(step.metadataJson?.dataScriptResults ?? []) as DataScriptResult[]}
              />
              <StepMcpTranscript entries={parseMcpTranscript(step.metadataJson?.mcpTranscript)} />
              {(() => {
                if (node?.type !== 'api_sequence') return null;
                const nodeData = (node.data ?? {}) as Record<string, unknown>;
                const nodeSteps = Array.isArray(nodeData._stepLabels)
                  ? (nodeData._stepLabels as Array<{
                      stepId: string;
                      method: string;
                      urlPattern: string | null;
                    }>)
                  : [];
                return (
                  <StepApiCalls
                    metadataJson={step.metadataJson}
                    nodeSteps={nodeSteps}
                    workflowId={workflowId}
                    runId={runId}
                    runStepId={step.id}
                  />
                );
              })()}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};
